<template lang="pug">
v-container(fluid)
  // Filtros y controles
  v-card.mb-4
    v-card-title
      | Reporte de Ventas Diarias
      v-spacer
      v-menu(
        ref="menu"
        v-model="menu"
        :close-on-content-click="false"
        :return-value.sync="filters.date"
        transition="scale-transition"
        offset-y
        max-width="290px"
        min-width="auto"
      )
        template(v-slot:activator="{ on, attrs }")
          v-text-field(
            v-model="dateText"
            label="Fecha"
            prepend-icon="mdi-calendar"
            readonly
            v-bind="attrs"
            v-on="on"
            hide-details
            class="mr-4 mt-n2"
            style="max-width: 200px"
          )
        v-date-picker(
          v-model="filters.date"
          no-title
          scrollable
          @input="$refs.menu.save(filters.date)"
        )
      v-btn(
        color="primary"
        @click="exportData"
        :loading="exporting"
      )
        v-icon(left) mdi-file-excel
        | Exportar

  // KPIs principales
  v-row(v-if="hasSalesData")
    v-col(cols="12" sm="6" md="3")
      v-card(outlined)
        v-card-text
          .text-overline.mb-1 Ventas Totales
          .text-h4 ${{ numberFormat(sales.totalRevenue || 0) }}
    v-col(cols="12" sm="6" md="3")
      v-card(outlined)
        v-card-text
          .text-overline.mb-1 Órdenes Totales
          .text-h4 {{ sales.totalOrders || 0 }}
    v-col(cols="12" sm="6" md="3")
      v-card(outlined)
        v-card-text
          .text-overline.mb-1 Ticket Promedio
          .text-h4 ${{ numberFormat((sales.totalRevenue && sales.totalOrders) ? sales.totalRevenue / sales.totalOrders : 0) }}
    v-col(cols="12" sm="6" md="3")
      v-card(outlined)
        v-card-text
          .text-overline.mb-1 Productos Vendidos
          .text-h4 {{ sales.totalQuantity || 0 }}

  v-alert(v-if="!hasSalesData" type="info" text dense class="mt-3")
    | Selecciona una fecha para ver el reporte de ventas diarias

  // Gráficos y datos detallados (solo si hay datos seleccionados)
  template(v-if="hasSalesData")
    // Distribución de ventas
    v-row.mt-4
      v-col(cols="12" md="6")
        v-card(outlined)
          v-card-title Distribución por Tipo de Orden
          v-card-text.chart-container
            v-skeleton-loader(
              v-if="loading"
              type="chart"
              :height="300"
            )
            v-chart-types(
              v-else-if="sales && sales.orderTypeSummary && sales.orderTypeSummary.length > 0" 
              :order-types="sales.orderTypeSummary" 
              :height="300"
            )
            v-alert(
              v-else
              type="info"
              text
              dense
              color="blue-grey lighten-5"
              class="my-2"
            )
              v-icon(left small) mdi-information
              | No hay datos de tipos de orden para mostrar en esta fecha
      v-col(cols="12" md="6")
        v-card(outlined)
          v-card-title Distribución por Medios de Pago
          v-card-text.chart-container
            v-skeleton-loader(
              v-if="loading"
              type="chart"
              :height="300"
            )
            pie-chart(
              v-else-if="paymentMethodsChartData && sales.paymentSummary && sales.paymentSummary.length > 0"
              :chart-data="paymentMethodsChartData"
              :chart-options="chartOptions"
              :height="300"
            )
            v-alert(
              v-else
              type="info"
              text
              dense
              color="blue-grey lighten-5"
              class="my-2"
            )
              v-icon(left small) mdi-information
              | No hay datos de medios de pago para mostrar en esta fecha
    
    // Productos vendidos y detalles
    v-row.mt-4
      v-col(cols="12" md="5")
        v-card(outlined)
          v-card-title Detalle de Productos Vendidos
          v-card-text
            v-data-table(
              :headers="productHeaders"
              :items="sales.productSummary || []"
              :items-per-page="10"
              :footer-props="{ 'items-per-page-options': [5, 10, 25, 50] }"
              sort-by="quantity"
              sort-desc
              dense
            )
              template(v-slot:item.total="{ item }")
                span ${{ numberFormat(item.total) }}
      v-col(cols="12" md="7")
        v-card(outlined)
          v-card-title Productos Más Vendidos (Top 10)
          v-card-text.chart-container
            v-skeleton-loader(
              v-if="loading"
              type="chart" 
              :height="300"
            )
            bar-chart(
              v-else-if="productsChartData && sales.productSummary && sales.productSummary.length > 0"
              :chart-data="productsChartData"
              :chart-options="barChartOptions" 
              :height="300"
            )
            v-alert(
              v-else
              type="info"
              text
              dense
              color="blue-grey lighten-5"
              class="my-2"
            )
              v-icon(left small) mdi-information
              | No hay datos de productos para mostrar en esta fecha

  // Modal para ver todos los productos
  v-dialog(v-model="showAllProducts" max-width="700px")
    v-card
      v-card-title Todos los Productos Vendidos
      v-card-text
        v-data-table(
          :headers="productHeaders"
          :items="sales && sales.productSummary ? sales.productSummary : []"
          :items-per-page="10"
          :footer-props="{'items-per-page-options': [10, 20, 50, -1]}"
        )
          template(v-slot:item.total="{ item }")
            | ${{ numberFormat(item.total) }}
      v-card-actions
        v-spacer
        v-btn(color="primary" text @click="showAllProducts = false") Cerrar
</template>

<script>
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { mapActions, mapGetters } from 'vuex'
import ChartOrderTypes from '@/components/charts/OrderTypesChart.vue'

// Importar los componentes de gráficos
import PieChart from '@/components/charts/PieChart.vue'
import BarChart from '@/components/dashboard/BarChart.vue'

export default {
  name: 'DailySalesReport',
  
  components: {
    'v-chart-types': ChartOrderTypes,
    'pie-chart': PieChart,
    'bar-chart': BarChart,
  },
  
  data: () => ({
    menu: false,
    filters: {
      date: ''
    },
    paymentMethods: [],
    showAllProducts: false,
    productHeaders: [
      { text: 'Producto', value: 'name', align: 'start' },
      { text: 'Cantidad', value: 'quantity', align: 'end' },
      { text: 'Total', value: 'total', align: 'end' }
    ],
    barChartOptions: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        yAxes: [{
          ticks: {
            beginAtZero: true
          }
        }]
      }
    },
    chartOptions: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            padding: 20,
            boxWidth: 10
          }
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const label = context.label || '';
              const value = context.parsed || context.raw;
              return `${label}: $${context.formattedValue}`;
            }
          }
        }
      }
    }
  }),
  
  computed: {
    ...mapGetters({
      sales: 'dailySalesReports/sales',
      totalItems: 'dailySalesReports/totalItems',
      loading: 'dailySalesReports/loading',
      exporting: 'dailySalesReports/exporting'
    }),
    
    dateText() {
      if (!this.filters.date) return ''
      
      // Usar formato directo para evitar problemas de zona horaria
      const [year, month, day] = this.filters.date.split('-').map(Number)
      const localDate = new Date(year, month - 1, day)
      
      return localDate.toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    },
    
    hasSalesData() {
      return this.sales && 
        this.sales.totalRevenue !== undefined && 
        this.sales.totalOrders !== undefined;
    },
    
    // Datos para el gráfico de medios de pago
    paymentMethodsChartData() {
      if (!this.sales || !this.sales.paymentSummary || !this.sales.paymentSummary.length) return null
      
      const labels = this.sales.paymentSummary.map(payment => payment.method)
      
      const data = this.sales.paymentSummary.map(payment => payment.amount)
      
      return {
        labels,
        datasets: [
          {
            data,
            backgroundColor: [
              '#B39DDB', // Morado (mismo que en Dashboard)
              '#F48FB1', // Rosa
              '#EF9A9A', // Rojo claro
              '#9FA8DA', // Azul claro
              '#81C784', // Verde claro
              '#FFE082'  // Amarillo claro
            ]
          }
        ]
      }
    },
    
    // Datos para el gráfico de productos
    productsChartData() {
      if (!this.sales || !this.sales.productSummary) return null
      
      // Tomar solo los 10 productos más vendidos para el gráfico
      const topProducts = [...this.sales.productSummary]
        .sort((a, b) => b.quantity - a.quantity)
        .slice(0, 10)
      
      return {
        labels: topProducts.map(item => item.name),
        datasets: [
          {
            label: 'Cantidad Vendida',
            backgroundColor: '#F48FB1', // Rosa (mismo que en Dashboard)
            data: topProducts.map(item => item.quantity)
          }
        ]
      }
    }
  },
  
  methods: {
    ...mapActions({
      showError: 'showError',
      fetchDaySales: 'dailySalesReports/fetchDailySales',
      exportSales: 'dailySalesReports/exportDailySales',
      fetchPaymentMethods: 'paymentMethods/fetch'
    }),
    
    formatDate(dateString) {
      if (!dateString) return ''
      
      // Si ya tiene formato de fecha, extraer los componentes
      const [year, month, day] = dateString.split('-').map(Number)
      const localDate = new Date(year, month - 1, day)
      
      // Formatear con date-fns usando locale español
      return format(localDate, 'EEEE, dd/MM/yyyy', { locale: es })
    },
    
    numberFormat(value, decimals = 0) {
      if (isNaN(value) || value === null || value === undefined) return '0'
      return value.toLocaleString('es-AR', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      })
    },
    
    getPaymentMethodName(payment) {
      // Si ya viene el nombre del método de pago desde el backend, lo usamos directamente
      if (typeof payment === 'string') {
        // Compatibilidad con versiones anteriores
        const method = this.paymentMethods.find(m => m._id === payment || m.id === payment)
        return method ? method.name : 'Desconocido'
      }
      
      // Si payment es el objeto completo
      return payment.name || 'Desconocido'
    },
    
    fetchData() {
      if (this.filters.date) {
        this.fetchDaySales({ date: this.filters.date })
          .catch(error => {
            this.showError('Error al cargar los datos del día: ' + error.message)
          })
      }
    },
    
    exportData() {
      if (this.filters.date) {
        this.exportSales({ date: this.filters.date })
          .catch(error => {
            this.showError('Error al exportar los datos: ' + error.message)
          })
      } else {
        this.showError('Por favor selecciona una fecha para exportar')
      }
    },
    
    loadPaymentMethods() {
      this.fetchPaymentMethods()
        .then(methods => {
          this.paymentMethods = methods || []
        })
        .catch(error => {
          console.error('Error al cargar medios de pago:', error)
          this.showError('Error al cargar los medios de pago: ' + error.message)
          this.paymentMethods = []
        })
    }
  },
  
  watch: {
    'filters.date': function() {
      this.fetchData()
    }
  },
  
  created() {
    // Fecha por defecto: hoy
    const today = new Date()
    this.filters.date = format(today, 'yyyy-MM-dd')
    
    // Cargar datos iniciales
    this.loadPaymentMethods()
    this.fetchData()
  }
}
</script>

<style scoped>
.v-card__text {
  padding-top: 12px;
}
.chart-container {
  padding: 16px;
}
</style>
